import {
  NUMERIC_VALUE_VALIDATION,
  URL_REGEX_ERROR_MESSAGE,
  URL_REGEX_PATTERN,
} from '../../../../shared/constants';

// Re-export for backward compatibility
export { NUMERIC_VALUE_VALIDATION };

export const FORM_NAME_VALIDATION = {
  MAX_LENGTH: 100,
  REGEX: /^[a-zA-Z0-9\s\-_]+$/,
  ERROR_MESSAGES: {
    REQUIRED: 'Form name is required',
    MAX_LENGTH: 'Form Name cannot exceed 100 characters',
    INVALID_CHARACTERS:
      'Only letters, numbers, spaces, hyphens, and underscores are allowed',
    INVALID_START: 'Form name must start with a letter or number',
    NO_HTML: 'HTML tags are not allowed in form name',
  },
} as const;

// Field metadata validation constants
export const FIELD_LABEL_VALIDATION = {
  MAX_LENGTH: 255,
  REGEX: /^[A-Za-z0-9\s\-_.():,?!;/\\#$[\]'"]+$/,
  START_REGEX: /^[A-Za-z0-9]/,
  ERROR_MESSAGES: {
    REQUIRED: 'Label is required',
    MAX_LENGTH: 'Label cannot exceed 255 characters',
    INVALID_START: 'Label must start with a letter or number',
    INVALID_CHARACTERS: 'Label contains unsupported characters',
    NO_HTML: 'HTML tags are not allowed in label',
    NO_BRACKETS: 'Angle brackets (< >) are not allowed in Field Label',
  },
} as const;

export const ELEMENT_ID_VALIDATION = {
  MAX_LENGTH: 50,
  REGEX: /^[a-z0-9_]+$/,
  START_REGEX: /^[a-z0-9]/,
  RESERVED_WORDS: ['requestor', 'requestee', 'loggedinuser'],
  ERROR_MESSAGES: {
    REQUIRED: 'Element ID is required',
    MAX_LENGTH: 'Element ID cannot exceed 50 characters',
    INVALID_START: 'Element ID must start with a lowercase letter or number',
    INVALID_CHARACTERS:
      'Only lowercase letters, numbers, and underscores are allowed (no spaces).',
    NOT_UNIQUE: 'Element ID must be unique within the form',
    NO_HTML: 'HTML tags are not allowed in Element ID',
    RESERVED_WORD: 'This Element ID is reserved and cannot be used',
  },
} as const;

export const PLACEHOLDER_VALIDATION = {
  MAX_LENGTH: 40,
  ERROR_MESSAGES: {
    MAX_LENGTH: 'Placeholder cannot exceed 40 characters',
    NO_HTML: 'HTML tags are not allowed in Placeholder',
  },
} as const;

export const HELPTEXT_VALIDATION = {
  MAX_LENGTH: 100,
  ERROR_MESSAGES: {
    MAX_LENGTH: 'Helptext cannot exceed 100 characters',
    NO_HTML: 'HTML tags are not allowed in Helper Text',
    NO_BRACKETS: 'Angle brackets (< >) are not allowed in Helper Text',
  },
} as const;

export const ERROR_MESSAGE_VALIDATION = {
  MAX_LENGTH: 255,
  ERROR_MESSAGES: {
    MAX_LENGTH: 'Error Message cannot exceed 255 characters',
    NO_HTML: 'HTML tags are not allowed in Error Message',
    NO_BRACKETS: 'Angle brackets (< >) are not allowed in Error Message',
  },
} as const;

export const FIXED_GROUP_OPTION_LABEL_VALIDATION = {
  MAX_LENGTH: 200,
  ERROR_MESSAGES: {
    REQUIRED: 'Option label is required',
    MAX_LENGTH: 'Option label cannot exceed 200 characters',
    NO_HTML: 'HTML tags are not allowed in Option label',
    NO_BRACKETS: 'Angle brackets (< >) are not allowed in Option label',
  },
} as const;

export const FIXED_GROUP_OPTION_VALUE_VALIDATION = {
  MAX_LENGTH: 200,
  ERROR_MESSAGES: {
    REQUIRED: 'Option value is required',
    MAX_LENGTH: 'Option value cannot exceed 200 characters',
  },
} as const;

export const TOOLTIP_VALIDATION = {
  MAX_LENGTH: 80,
  ERROR_MESSAGES: {
    MAX_LENGTH: 'Tooltip cannot exceed 80 characters',
    NO_HTML: 'HTML tags are not allowed in Tooltip',
    NO_BRACKETS: 'Angle brackets (< >) are not allowed in Tooltip',
  },
} as const;

export const DESCRIPTION_VALIDATION = {
  MAX_LENGTH: 300,
  REGEX: /^[A-Za-z0-9 \-_.,:;()\n]+$/,
  ERROR_MESSAGES: {
    MAX_LENGTH: 'Form Description cannot exceed 300 characters',
    INVALID_CHARACTERS:
      'Only letters, numbers, spaces, and basic punctuation (hyphens, underscores, periods, commas, colons, parentheses) are allowed',
    NO_HTML: 'HTML tags are not allowed in description',
  },
} as const;

export const URL_SLUG_VALIDATION = {
  MAX_LENGTH: 100,
  REGEX: /^[a-z0-9-]+$/,
  RESERVED_PREFIX: 'sav-da-',
  RESERVED_WORDS: [
    'admin',
    'api',
    'login',
    'create',
    'edit',
    'delete',
    'update',
    'logout',
    'signup',
    'register',
    'auth',
    'forgot-password',
    'reset-password',
    'verify-email',
    'dashboard',
    'settings',
    'config',
    'system',
    'graphql',
    'webhook',
    'internal',
    'assets',
    'static',
    'images',
    'js',
    'css',
    'fonts',
    'favicon.ico',
    'robots.txt',
    'sitemap.xml',
    'debug',
    'status',
    'health',
    'metrics',
    'docs',
    'swagger',
    'home',
    'about',
    'contact',
    'terms',
    'privacy',
    'help',
    'support',
    'search',
    'blog',
    'news',
    'view',
    'requestor',
    'requestee',
    'loggedinuser',
    'sav-da',
  ],
  ERROR_MESSAGES: {
    REQUIRED: 'URL slug is required',
    MAX_LENGTH: 'URL cannot exceed 100 characters',
    INVALID_CHARACTERS:
      'Only lowercase letters, numbers, and hyphens are allowed',
    SPACES_NOT_ALLOWED: 'Spaces are not allowed in URL slug',
    RESERVED_WORD: 'This URL slug is reserved and cannot be used',
    RESERVED_PREFIX: "URL slug cannot start with 'sav-da-'",
    LEADING_TRAILING_HYPHENS: 'URL slug cannot start or end with hyphens',
    UNIQUE_REQUIRED: 'URL slug must be unique across all forms',
  },
} as const;

export const TARGET_ENTITY_VALIDATION = {
  VALID_CATEGORIES: ['Users', 'Orchestration'],
  ERROR_MESSAGES: {
    REQUIRED: 'Form category is required',
    INVALID_SELECTION: 'Please select a valid form category',
  },
} as const;

// Actions for Users category
export const USER_ACTIONS = {
  CREATE_USER: 'CREATE_USER',
  CREATE_USER_REQUEST: 'CREATE_USER_REQUEST',
  UPDATE_USER: 'UPDATE_USER',
  UPDATE_USER_REQUEST: 'UPDATE_USER_REQUEST',
} as const;

export const FORM_ACTION_VALIDATION = {
  VALID_ACTIONS: {
    Users: [
      USER_ACTIONS.CREATE_USER,
      USER_ACTIONS.CREATE_USER_REQUEST,
      USER_ACTIONS.UPDATE_USER,
      USER_ACTIONS.UPDATE_USER_REQUEST,
    ],
  },
  ERROR_MESSAGES: {
    REQUIRED: 'Form action is required',
    INVALID_SELECTION: 'Please select a valid form action',
    CATEGORY_MISMATCH: 'Selected action is not valid for the chosen category',
  },
} as const;

export const APPROVAL_WORKFLOW_VALIDATION = {
  ERROR_MESSAGES: {
    REQUIRED: 'Approval workflow is required',
    INVALID_SELECTION: 'Please select a valid approval workflow',
    INACTIVE_WORKFLOW: 'Selected workflow is not active',
  },
} as const;

export const TERMS_LINK_VALIDATION = {
  REGEX: URL_REGEX_PATTERN,
  ERROR_MESSAGES: {
    REQUIRED: 'Terms link is required',
    INVALID_FORMAT: URL_REGEX_ERROR_MESSAGE,
  },
} as const;

export const TNC_MESSAGE_VALIDATION = {
  REGEX: /^[^{}]*{\s*[^{}\s][^{}]*}[^{}]*$/,
  ERROR_MESSAGES: {
    REQUIRED: 'Inline message is required',
    INVALID_FORMAT:
      'Message must contain text wrapped in {} to create a clickable link',
  },
} as const;

export const RICH_TEXT_VALIDATION = {
  ERROR_MESSAGES: {
    REQUIRED: 'Content is required',
  },
} as const;

export const REGEX_VALIDATION = {
  MAX_LENGTH: 255,
  MIN_LENGTH: 1,
  ERROR_MESSAGE_MAX_LENGTH: 255,
} as const;
